import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, List, ListItem, ListItemText, Typography } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import styled from '@emotion/styled';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { formatDate } from '../../utils/dateFormat';
import { useTranslation } from 'react-i18next';
import { Email } from './EmailList';

const AttachmentsContainer = styled(Box)`
  position: absolute;
  top: 16px;
  right: 56px;
  display: flex;
  gap: 8px;
  align-items: center;
`;

const EmailContent = styled(Box)`
  padding: 20px;
  img {
    max-width: 100%;
    height: auto;
  }
`;

// 添加附件弹窗组件
const AttachmentDialog = styled(Dialog)`
  .MuiDialog-paper {
    width: 100%;
    max-width: 500px;
  }
`;

interface EmailDetailProps {
  email: Email;
  onClose: () => void;
}

// 在组件中添加图片处理函数
const processHtmlContent = (content: string) => {
  // 创建一个 DOM 解析器
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  
  // 处理所有图片元素
  doc.querySelectorAll('img').forEach(img => {
    // 添加错误处理
    img.setAttribute('onerror', 'this.onerror=null;this.src="/images/image-error.png"');
    // 添加加载中效果
    img.setAttribute('loading', 'lazy');
    // 添加样式
    img.classList.add('rounded-lg');
  });
  
  return doc.body.innerHTML;
};

export const EmailDetail: React.FC<EmailDetailProps> = ({ email, onClose }) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <Box position="relative">
      {email.attachments && email.attachments.length > 0 && (
        <>
          <AttachmentsContainer>
            <Tooltip title={t('sections.common.inbox.attachments')}>
              <IconButton
                onClick={() => setDialogOpen(true)}
                size="small"
                color="primary"
              >
                <GetAppIcon />
                <Typography variant="caption" sx={{ ml: 0.5 }}>
                  {email.attachments.length}
                </Typography>
              </IconButton>
            </Tooltip>
          </AttachmentsContainer>

          <AttachmentDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
          >
            <DialogTitle>{t('sections.common.inbox.attachments')}</DialogTitle>
            <DialogContent>
              <List>
                {email.attachments.map((attachment, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        href={attachment.url}
                        download={attachment.filename}
                      >
                        <GetAppIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={attachment.filename}
                      secondary={formatFileSize(attachment.size)}
                    />
                  </ListItem>
                ))}
              </List>
            </DialogContent>
          </AttachmentDialog>
        </>
      )}
      
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        className="overflow-hidden bg-white border-t border-gray-200"
      >
        <div className="p-4">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 space-y-2">
              {/* 发件人 */}
              <p className="text-gray-900">
                <span className="font-medium">{t('sections.common.inbox.from')}：</span>
                {email.senderName || email.sender}
              </p>
              
              {/* 主题 */}
              <p className="text-gray-900">
                <span className="font-medium">{t('sections.common.inbox.subject')}：</span>
                {email.subject || t('sections.common.inbox.noSubject')}
              </p>
              
              {/* 发送时间 */}
              <p className="text-gray-900">
                <span className="font-medium">{t('sections.common.inbox.received')}：</span>
                {formatDate(email.created_at)}
              </p>
            </div>
            
            <button
              onClick={onClose}
              className="p-1 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* 分隔线 */}
          <div className="h-px bg-gray-200 my-4" />

          {/* 邮件内容 - 使用 dangerouslySetInnerHTML 渲染 HTML */}
          <EmailContent 
            dangerouslySetInnerHTML={{ 
              __html: processHtmlContent(email.content)
            }}
            sx={{
              '& img': {
                maxWidth: '100%',
                height: 'auto'
              }
            }}
          />
        </div>
      </motion.div>
    </Box>
  );
}; 